import { call, put, debounce, throttle, takeLatest, select, delay } from 'redux-saga/effects'
import jwtDecode from 'jwt-decode'
import qs from 'querystring'
import downloadString from 'lib/download-string'
import { webApi, api } from 'lib/api'
import { errorWrap } from 'lib/saga-helpers'
import { buildBooleanSearchPrompt } from 'lib/boolean-search-prompt'
import { buildSuggestedSearchesPrompt } from 'lib/suggested-searches-prompt'
import { writeSuggestionCache } from 'lib/suggestion-cache'
import { runAdHocAssistantPrompt } from 'store/assistant/run-ad-hoc-prompt'
import { captureException } from 'client/lib/sentry'
import { constants, loadUser as loadUserAction, fetchDiscount as fetchDiscountAction, toggleModal } from './actions'
import { initializePostHog, resetPostHog, setPostHogPersonMetadata } from 'client/lib/posthog'
import * as analytics from 'lib/analytics'
import * as rewardful from 'lib/rewardful'
import { fetchDropDownNotificationItems, fetchNotifications } from 'store/notifications/actions'
import { assistantUpdateUsage } from 'store/assistant/actions'
import { fetchMcpUsage } from 'store/mcp-usage/actions'
import { handleReferral } from 'store/ui/actions'
import { userIsAtLeast } from 'lib/user'
import toast from 'react-hot-toast'
import { errorToast, infoToast, successToast } from 'lib/toast'
import { PLANS } from '../../../server/lib/constants'
import { getLocalStorage } from '../../lib/get-local-storage'
import { getSessionStorage } from '../../lib/get-session-storage'
import { clearAuthTokens, clearUserToken, readApiToken, setApiToken } from 'lib/api-token'

const SERVER = typeof window === 'undefined'

export const selectors = {
  referencesToExport: ({ ui }) => ui.referencesToExport,
  referralPartner: ({ ui }) => ui.referralPartner,
  agAvailabilityQueue: ({ ui }) => ui.agAvailabilityQueue,
  fullTextLinkQueue: ({ ui }) => ui.fullTextLinkQueue
}

export function* getNewToken() {
  try {
    const { data: newToken } = yield call(webApi.get, '/auth/api_token')
    setApiToken(newToken)
  } catch (e) {
    console.error('could not get token from API, bad scitewebapp JWT?', e)
  }
}

//
// For the case where the user has a token
// not minted by the Node backend.
//
// In this case the token claims are not necesseraly
// derived from the user object. This can be used for
// anonymous users to make claims on restricted API resources,
// and by authenticated users to escalate their permissions.
//
function* loadAnonToken({ payload: token }) {
  const sessionStorage = getSessionStorage()
  sessionStorage?.setItem('anonApiToken', token)
}

//
// Finalize user setup once they are loaded from
// express session and have correct API token
//
function* _setupUser(user) {
  // Org-license sessions can produce a truthy `user` without real auth (no email),
  // and notification endpoints require an authenticated user, so gate on it here.
  if (user.email) {
    yield put(fetchNotifications())
    yield put(fetchDropDownNotificationItems())
  }
  try {
    yield call(checkOrganizationLicense)
  } catch (e) {
    console.error(e)
  }

  //
  // Generally for any ag-sso accounts we want to show the AG link resolver
  // But this caused weirdness for a re-seller because they want to demo their own,
  //    which is configured on their license but overriden because one of their accounts
  //    got auto-magically upgraded to ag-sso.
  // The org license check is a hotfix to unblock them until we find a longer term solution.
  //
  if (user.signupType === 'ag-sso' && user.organizationLicense !== 'sunmedia-reseller-partner-in-japan') {
    yield put(handleReferral('reprintsdesk.com'))
  }
  analytics.setUser(user)
}

export function* loadUser({ payload: { query = {} } = {} }) {
  if (SERVER) {
    return
  }

  const { location } = window
  const { autologin, ...restQuery } = query
  let user = null

  try {
    const res = yield call(webApi.get, '/users/me')
    user = res.data
    if (user.assistantUsage) {
      yield put(assistantUpdateUsage(user.assistantUsage))
    }
  } catch (e) {
    const status = e?.response?.status
    if (status === 404) {
      clearUserToken()
    }
    yield put({
      type: constants.LOAD_USER_FAILURE,
      error: e
    })
  }

  if (autologin === 'azure' && !userIsAtLeast(user, PLANS.premium)) {
    //
    // Remove autologin from the query so we don't accidentally
    // end up in an infinite loop somehow
    //
    const redirectQuery = qs.stringify(restQuery)
    if (redirectQuery) {
      window.history.replaceState(null, null, window.location.pathname + `?${redirectQuery}`)
    } else {
      window.history.replaceState(null, null, window.location.pathname)
    }
    location.pathname = '/api/auth/azuread'
    return
  }

  if (!user) {
    yield put({ type: constants.LOAD_USER_FAILURE })
    // Anonymous visitors may still be on an IP-authenticated org network.
    // Fire the license check so PostHog gets the org-group association
    // for the otherwise-unauthenticated session. Best-effort: a failure here
    // (e.g. 403 from the license endpoint) shouldn't break page load for
    // anonymous visitors.
    try {
      yield call(checkOrganizationLicense)
    } catch (e) {
      console.error(e)
    }
    return
  }

  try {
    //
    // Read the token only now, after /users/me has returned: a concurrent
    // request rejected as invalid_token clears storage mid-flight, and a value
    // captured before the await would still look fine and skip the refresh,
    // leaving a logged-in UI with anonymous API access (SCI-3965). An
    // undecodable token behaves like none and falls through to getNewToken.
    //
    const currentToken = readApiToken()
    let currentClaims = {}
    try {
      currentClaims = currentToken ? jwtDecode(currentToken) : {}
    } catch {
      // mint a new token below
    }
    const differentUser = currentClaims?.email !== user.email
    const differentPlan = currentClaims?.plan !== user.subscription?.plan

    if (
      currentToken &&
      currentClaims !== null &&
      !differentUser &&
      !differentPlan &&
      currentClaims.version &&
      currentClaims.version === CONFIG.apiWebUser.jwtVersion
    ) {
      yield put({
        type: constants.LOAD_USER_SUCCESS,
        payload: user
      })
      yield _setupUser(user)
      return
    }

    yield getNewToken()
    yield put({
      type: constants.LOAD_USER_SUCCESS,
      payload: user
    })
    yield _setupUser(user)
  } catch (e) {
    yield put({
      type: constants.LOAD_USER_FAILURE,
      error: e
    })
    captureException(e, { extra: { context: 'Could not load user' } })
  }
}

function* saveUser({ payload: { slug, ...rest } }) {
  const { data: savedUser } = yield call(webApi.patch, `/users/${slug}`, {
    ...rest
  })
  yield put({ type: constants.SAVE_USER_SUCCESS, data: savedUser })
  yield put(loadUserAction())
  successToast({ message: 'Account settings saved.', durationMs: 3000 })
}

export function* logout({ payload: { history } = {} }) {
  // Clear first: when every request is failing, this is the only exit (SCI-3965).
  clearAuthTokens()
  try {
    yield call(webApi.get, '/users/logout')
  } catch (e) {
    console.error('server logout failed, client session cleared anyway', e)
  }
  yield put({
    type: constants.LOGOUT_SUCCESS
  })

  analytics.event(
    {
      category: 'User',
      action: 'Logged out'
    },
    { pushToPosthog: false }
  )
  resetPostHog()
  history?.push('/')
}

function* deleteUser({ payload: { history } }) {
  const infoToastId = infoToast({
    message: 'Deleting your account, hang tight...',
    durationMs: 20000
  })

  try {
    try {
      yield call(webApi.post, '/checkout/subscription/delete', {
        deleteAccount: true
      })
    } catch (e) {
      // Don't block account deletion on subscription-cancel failures —
      // the backend will detach the Stripe record during user purge, and
      // any orphaned Stripe subscription can be reconciled separately.
      console.error('Subscription cancel failed during account delete:', e)
    }
    yield call(api.delete, '/users/delete')
    clearAuthTokens()
    yield put({
      type: constants.DELETE_USER_SUCCESS
    })

    successToast({
      message: 'Successfully deleted your account.',
      durationMs: 3000
    })

    history.push('/')
    analytics.event({
      category: 'User',
      action: 'user deleted'
    })
  } finally {
    toast.dismiss(infoToastId)
  }
}

function* fetchOnboarding({ payload: slug }) {
  const { data } = yield call(api.get, `/users/slug/${slug}/onboarding`)

  yield put({
    type: constants.FETCH_ONBOARDING_SUCCESS,
    payload: data
  })
}

export function* getEntitlements() {
  const { data } = yield call(api.get, '/entitlements/entitlement')
  yield put({
    type: constants.GET_ENTITLEMENTS_SUCCESS,
    payload: data
  })
}

export function* saveReferrals({ payload: { referrals } }) {
  try {
    const { data } = yield call(api.post, '/users/referral', { referrals })
    yield put({
      type: constants.SAVE_REFERRALS_SUCCESS,
      payload: data
    })
  } catch (e) {
    console.error('failed saving referrals for user', e)
    yield put({
      type: constants.SAVE_REFERRALS_FAILURE
    })
  }
  try {
    yield call(webApi.put, '/checkout/subscription/grant-referral-benefits')
    yield loadUser()
  } catch (e) {
    console.error('failed granting referral benefits for user', e)
  }
}

export function* checkOrganizationLicense() {
  const {
    data: { organizationLicense }
  } = yield call(webApi.get, '/auth/check_license')
  const license = organizationLicense || { hasLicense: false }
  license.licenseChecked = true
  const user = yield select(({ ui }) => ui.user)
  if (organizationLicense) {
    setPostHogPersonMetadata(user, license)
  }
  // API-only licenses (Model 2, SCI-3885) grant self-serve API access but NOT premium
  // platform access. The ~20 platform gates key off `organizationLicense.hasLicense`,
  // so treat an API-only license as no platform license at this single point where the
  // license enters the store — `apiOnly`/`apiAccessEnabled` are preserved so the API
  // console still unlocks (via planUnlocksApi's apiOnly branch). Analytics above still
  // sees the real affiliation; only the store copy is suppressed.
  const apiOnly = license.apiOnly ?? license.api_only
  const payload = license.hasLicense && apiOnly ? { ...license, hasLicense: false } : license
  yield put({
    type: constants.CHECK_PREMIUM_IP_SUCCESS,
    payload
  })
}

function* reenableSubscription() {
  try {
    const { data } = yield call(webApi.post, '/checkout/subscription/reenable')
    yield put({
      type: constants.REENABLE_SUBSCRIPTION_SUCCESS,
      payload: data.subscription
    })
    // The server only reports `changed: true` when auto-renew actually flipped.
    // A no-op used to toast success anyway, so nothing on screen matched the
    // message the user had just been given (SCI-3461).
    const label = data.subscription?.trial ? 'Trial' : 'Subscription'
    if (data.changed === false) {
      infoToast({
        message: data.message || 'Your subscription is already set to renew automatically.',
        durationMs: 4000
      })
      analytics.event({
        category: 'Auto-renew banner',
        action: 'Turn Back On No-op',
        label
      })
    } else {
      successToast({
        message: 'Auto-renew has been turned back on.',
        durationMs: 4000
      })
      analytics.event({
        category: 'Auto-renew banner',
        action: 'Turn Back On',
        label
      })
    }
  } catch (e) {
    const message =
      e?.response?.data?.error || 'Failed to turn auto-renew back on. Please try again or contact support.'

    yield put({ type: constants.REENABLE_SUBSCRIPTION_ERROR })
    errorToast({
      message,
      durationMs: 4000
    })
    analytics.event({
      category: 'Auto-renew banner',
      action: 'Turn Back On Error',
      label: message
    })
  }
}

function* endTrialEarly() {
  try {
    const { data } = yield call(webApi.post, '/checkout/subscription/end-trial')
    yield put({
      type: constants.END_TRIAL_EARLY_SUCCESS,
      payload: data.subscription
    })
    // Reload the user (refreshes /me + the JWT plan claim) and refetch MCP usage so the
    // panel reflects the now-active subscription and the unlocked full monthly allowance.
    yield put(loadUserAction())
    yield put(fetchMcpUsage())
    successToast({
      message: 'Your subscription is active. Your full monthly credits are now available.',
      durationMs: 4000
    })
    analytics.event({
      category: 'Subscription',
      action: 'Start Subscription Now Success'
    })
  } catch (e) {
    const message =
      e?.response?.data?.error || 'We could not start your subscription. Please try again or contact support.'

    yield put({ type: constants.END_TRIAL_EARLY_ERROR })
    errorToast({
      message,
      durationMs: 6000
    })
    analytics.event({
      category: 'Subscription',
      action: 'Start Subscription Now Error',
      label: message
    })
  }
}

function* applyDiscount({ payload }) {
  const infoToastId = infoToast({
    message: 'Applying coupon, hang tight...',
    durationMs: 20000
  })
  try {
    yield call(webApi.post, '/checkout/subscription/apply-discount', {
      ...payload
    })
    toast.dismiss(infoToastId)
    successToast({
      message: `Successfully applied ${payload.percentAmountOff}% off discount to your account, this will take effect on the next invoice.`,
      durationMs: 4000
    })
    yield put({
      type: constants.APPLY_DISCOUNT_SUCCESS
    })
    yield put(fetchDiscountAction())
  } catch (e) {
    toast.dismiss(infoToastId)
    errorToast({
      message: 'Failed to apply discount. Please contact customersupport@researchsolutions.com.',
      durationMs: 4000
    })
    yield put({
      type: constants.APPLY_DISCOUNT_FAILURE,
      payload: 'An error occurred applying the discount, please contact support or try again later.'
    })
  }
}

function* fetchDiscount() {
  const { data } = yield call(webApi.get, '/checkout/subscription/fetch-discount')
  yield put({
    type: constants.FETCH_DISCOUNT_SUCCESS,
    payload: data
  })
}

function* completeOnboardingStep({ payload: { category, step, email } }) {
  const res = yield call(api.put, '/users/onboarding', {
    category,
    step,
    email
  })
  yield put({
    type: constants.LOAD_USER_SUCCESS,
    payload: res.data
  })
}

function* fetchAutocompletes({ payload: { term, field, includeFields } }) {
  const params = {}

  if (term?.length && term.length > 1000) {
    console.warn('Too many characters to autocomplete')
    return
  }

  if (term) params.prefix = term.slice(0)
  if (field) params.field = field
  if (includeFields) params.include_fields = includeFields

  const {
    data: { autocompletes }
  } = yield call(api.get, '/search/autocompletes', { params })

  yield put({
    type: constants.FETCH_AUTOCOMPLETES_SUCCESS,
    payload: { autocompletes, field }
  })
}

const BOOLEAN_GEN_TIMEOUT = 60 * 1000

// Extract a JSON object from the LLM response. The prompt asks for a single
// fenced ```json``` code block, but tolerate a bare JSON object too.
const tryParseJSON = text => {
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

export const parseBooleanVariants = responseText => {
  if (!responseText || typeof responseText !== 'string') {
    console.error('[BooleanSearch] empty LLM response')
    throw new Error('No suggestions were returned. Please try again.')
  }

  const trimmed = responseText.trim()

  // The backend short-circuits to a literal "Error: ..." string when the LLM
  // call itself fails (see scite-qa/sciteqa/lib/generative_model.py). Surface
  // that as a clean failure rather than trying to JSON-parse it.
  if (/^Error:/i.test(trimmed)) {
    console.error('[BooleanSearch] backend returned error string:', trimmed)
    throw new Error('Boolean generation failed. Please try again.')
  }

  const candidates = []

  // 1. Fenced ```json ... ``` (or ``` ... ```) block.
  const fenceMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/)
  if (fenceMatch) candidates.push(fenceMatch[1].trim())

  // 2. The whole response, as-is.
  candidates.push(trimmed)

  // 3. The substring from the first "{" to the last "}" — catches the case
  //    where the model wraps a bare object in surrounding prose.
  const first = trimmed.indexOf('{')
  const last = trimmed.lastIndexOf('}')
  if (first !== -1 && last > first) {
    candidates.push(trimmed.slice(first, last + 1))
  }

  // 4. Recovery for a known LLM failure mode: the model writes
  //    `"variants": <obj>, <obj>, ...` and forgets the array brackets.
  //    For every candidate so far, try a variant that wraps the value of
  //    "variants" in [ ... ] when it's not already an array.
  const withArrayBrackets = candidate => {
    return candidate.replace(/("variants"\s*:\s*)(?!\[)(\{[\s\S]*\})(\s*\}\s*)$/, '$1[$2]$3')
  }
  for (const candidate of candidates.slice()) {
    const recovered = withArrayBrackets(candidate)
    if (recovered !== candidate) candidates.push(recovered)
  }

  let parsed = null
  for (const candidate of candidates) {
    parsed = tryParseJSON(candidate)
    if (parsed) break
  }

  if (!parsed) {
    console.error('[BooleanSearch] failed to parse LLM response. Raw response:\n', responseText)
    throw new Error('Could not read the boolean suggestions. Please try again.')
  }

  // Recovery for the single-variant case: when the model returns only one
  // suggestion, it sometimes puts the variant object directly under
  // "variants" instead of wrapping it in a one-element array.
  if (
    parsed.variants &&
    typeof parsed.variants === 'object' &&
    !Array.isArray(parsed.variants) &&
    typeof parsed.variants.query === 'string'
  ) {
    parsed.variants = [parsed.variants]
  }

  const variants = Array.isArray(parsed?.variants) ? parsed.variants : []
  const cleaned = variants
    .map(v => ({
      query: typeof v?.query === 'string' ? v.query.trim() : '',
      label: typeof v?.label === 'string' ? v.label.trim() : '',
      explanation: typeof v?.explanation === 'string' ? v.explanation.trim() : ''
    }))
    .filter(v => v.query.length > 0)

  if (cleaned.length === 0) {
    console.error('[BooleanSearch] no usable variants in parsed response:', parsed)
    throw new Error('No usable boolean queries were generated. Please try again.')
  }

  return cleaned
}

function* generateBooleanSearch({ payload: { term } }) {
  const trimmed = (term || '').trim()
  if (!trimmed) {
    yield put({
      type: constants.GENERATE_BOOLEAN_SEARCH_FAILURE,
      payload: { error: 'Please enter a search term first.' }
    })
    return
  }

  let result
  try {
    result = yield* runAdHocAssistantPrompt({
      prompt: buildBooleanSearchPrompt(trimmed),
      isCancelled: ({ ui }) => !ui.search.booleanSuggestions?.loading,
      timeoutMs: BOOLEAN_GEN_TIMEOUT
    })
  } catch (e) {
    const status = e.response?.status
    if (status === 426) {
      yield put(toggleModal('upgrade', true))
      yield put({
        type: constants.GENERATE_BOOLEAN_SEARCH_FAILURE,
        payload: { error: 'Upgrade required to generate boolean searches.' }
      })
      return
    }
    if (status === 429) {
      yield put({
        type: constants.GENERATE_BOOLEAN_SEARCH_FAILURE,
        payload: { error: 'Too many requests. Please wait a moment and try again.' }
      })
      return
    }
    const error =
      e?.name === 'TimeoutError'
        ? 'Generation timed out. Please try again.'
        : e.response?.data?.error || 'Could not generate boolean searches. Please try again.'
    yield put({
      type: constants.GENERATE_BOOLEAN_SEARCH_FAILURE,
      payload: { error }
    })
    return
  }

  if (result.status === 'CANCELLED' || result.status === 'REVOKED') {
    return
  }

  if (result.status === 'FAILURE') {
    yield put({
      type: constants.GENERATE_BOOLEAN_SEARCH_FAILURE,
      payload: { error: result.error || 'Could not generate boolean searches. Please try again.' }
    })
    return
  }

  let variants
  try {
    variants = parseBooleanVariants(result.responseText)
  } catch (e) {
    yield put({
      type: constants.GENERATE_BOOLEAN_SEARCH_FAILURE,
      payload: { error: e.message }
    })
    return
  }

  yield put({
    type: constants.GENERATE_BOOLEAN_SEARCH_SUCCESS,
    payload: { variants, originalTerm: trimmed }
  })
}

const SUGGESTED_SEARCHES_TIMEOUT_MS = 30 * 1000
const MAX_SUGGESTED_SEARCHES = 3

// Generate boolean-search suggestions for the empty search input, seeded from
// the user's recent topics. Mirrors generateSuggestedPromptsSaga (assistant),
// but produces boolean queries parsed with the same parser as the search-box
// "Generate a boolean search" dropdown. `key` dedupes per seed set.
export function* generateSuggestedSearches({ payload: { topics, key, exclude = [] } }) {
  const seedTopics = (topics || []).filter(Boolean)
  if (seedTopics.length === 0) {
    yield put({
      type: constants.GENERATE_SUGGESTED_SEARCHES_FAILURE,
      payload: { key, error: 'No recent topics to suggest from.' }
    })
    return
  }

  let result
  try {
    result = yield* runAdHocAssistantPrompt({
      prompt: buildSuggestedSearchesPrompt(seedTopics, MAX_SUGGESTED_SEARCHES, exclude),
      isCancelled: ({ ui }) => ui.search.suggestedSearches?.key !== key,
      timeoutMs: SUGGESTED_SEARCHES_TIMEOUT_MS
    })
  } catch (e) {
    yield put({
      type: constants.GENERATE_SUGGESTED_SEARCHES_FAILURE,
      payload: { key, error: e?.message || 'Could not generate suggested searches.' }
    })
    return
  }

  if (result.status === 'CANCELLED' || result.status === 'REVOKED') return

  if (result.status === 'FAILURE') {
    yield put({
      type: constants.GENERATE_SUGGESTED_SEARCHES_FAILURE,
      payload: { key, error: result.error || 'Could not generate suggested searches.' }
    })
    return
  }

  let items
  try {
    items = parseBooleanVariants(result.responseText).slice(0, MAX_SUGGESTED_SEARCHES)
  } catch (e) {
    yield put({
      type: constants.GENERATE_SUGGESTED_SEARCHES_FAILURE,
      payload: { key, error: e.message }
    })
    return
  }

  if (items.length > 0) yield call(writeSuggestionCache, 'search', key, items)

  yield put({
    type: constants.GENERATE_SUGGESTED_SEARCHES_SUCCESS,
    payload: { key, items }
  })
}

function* exportReferences({ payload: { params } }) {
  const referencesToExport = yield select(selectors.referencesToExport)
  const { data: text, headers } = yield call(api.post, '/papers/export', referencesToExport, { params })
  downloadString(text, headers['x-file-name'])
  yield put({ type: constants.EXPORT_REFERENCES_SUCCESS })
}

function* trialActivated() {
  yield call(webApi.post, '/users/activate-trial')
  yield put({
    type: constants.TRIAL_ACTIVATED_SUCCESS
  })
  // Reload the user so subscription.renewDate (written server-side by the
  // trial-success endpoint) replaces the optimistic stub above. Without this,
  // the welcome banner reads renewDate from the pre-trial user and shows
  // "0 days remaining" until the next manual refresh (SCI-2089).
  yield put(loadUserAction())
}

function* modalToggled({ payload: { name } }) {
  const key = `${name}ModalShow`
  const currentValue = yield select(({ ui }) => ui[key])

  if (currentValue) {
    return
  }

  yield delay(600)

  yield put({ type: constants.CLEAR_MODAL_FROM_CONTEXT })
}

// FERPA-covered members are never opted into targeting/advertising cookies regardless of
// their banner choice — their activity is handled as education records (no commercial
// reuse). We honour the user's displayed selection in the UI but strip `targeting` from the
// preference that drives analytics/rewardful/PostHog. (Core PostHog events still flow in
// cookieless mode, which powers the institution's own usage reports.)
const stripTargetingForFerpa = (cookiePreference, user) => {
  if (!user || !user.ferpaCovered) {
    return cookiePreference
  }
  if (cookiePreference === 'accepted') {
    return ['performance', 'functional'].join(',')
  }
  return (
    (cookiePreference || '')
      .split(',')
      .filter(category => category && category !== 'targeting')
      .join(',') || 'rejected'
  )
}

// Module-level, mirroring the `initialized` guard in rewardful.js: LOAD_USER_SUCCESS can fire
// more than once per session (saveUser re-dispatches loadUser; MCPOAuthAuthorize dispatches on
// every visibilitychange), and re-running the settle step on each one would inflate GA pageviews
// and repeatedly re-enter posthog.init (SCI-2865).
let ferpaConsentSettled = false
let ferpaConsentDeferred = false

// Test-only: reset the module-level guards above between cases.
export const __resetFerpaConsentGuardsForTests = () => {
  ferpaConsentSettled = false
  ferpaConsentDeferred = false
}

export function* loadCookiePreference({ payload: { isDoNotSell } = {} } = {}) {
  const localStorage = getLocalStorage()
  const cookiePreference = localStorage?.getItem('cookiePreference')

  if (isDoNotSell) {
    yield call(webApi.get, '/users/donotsell')
  }

  yield put({
    type: constants.SET_COOKIE_PREFERENCE,
    payload: cookiePreference || 'unknown'
  })

  if (!cookiePreference || cookiePreference === 'unknown') {
    return
  }

  const user = yield select(({ ui }) => ui.user)
  const hasUser = yield select(({ ui }) => ui.hasUser)
  const effectivePreference = stripTargetingForFerpa(cookiePreference, user)

  // `hasUser` (hydrated from SSR's `!!req.user`) means a logged-in session exists before the
  // full user record — and therefore `ferpaCovered` — has loaded. Granting/identifying now and
  // revoking once LOAD_USER_SUCCESS arrives still leaks one consented, identified pageview per
  // load, so defer analytics/PostHog too — not just Rewardful — to
  // reconcileFerpaConsentOnUserLoad below (SCI-2865). Anonymous IP-matched visitors have no such
  // signal to defer on — `hasUser` is false for them, and their `ferpaCovered` status is only
  // knowable once /users/me resolves regardless, so they keep the (smaller, already-accepted)
  // race below.
  if (hasUser && !user) {
    ferpaConsentDeferred = true
    return
  }

  yield call(analytics.setup, effectivePreference)
  if (user) {
    // Rewardful injects a third-party script with no way to un-inject it, so we only ever call
    // it once `user` is actually known — deferred to reconcileFerpaConsentOnUserLoad below for
    // anonymous IP-matched visitors, who reach this point with `user` still null.
    yield call(rewardful.initialize, effectivePreference)
  }
  analytics.pageView()

  yield call(initializePostHog, effectivePreference)

  //
  // PostHog can only initialize once a cookie preference exists, which may not
  // happen until after the user is already loaded and identified. When a visitor
  // arrives pre-authenticated on first page load — e.g. the Article Galaxy SSO
  // entry path — `_setupUser` runs immediately and its `analytics.setUser` call
  // tries to identify before PostHog is ready, then gives up after a ~10s wait.
  // Because `setUser` dedupes on the unchanged user object, identify is never
  // retried once consent is granted, so the person stays anonymous (SCI-2961).
  //
  // Now that PostHog is initialized, re-run the person/group association from
  // current state. `setPostHogPersonMetadata` no-ops when there's neither a user
  // nor an org license, and still honours opt-out internally (cookie-rejected
  // org users get a stable anonymous hash; rejected non-org users get nothing).
  //
  const organizationLicense = yield select(({ ui }) => ui.organizationLicense)
  yield call(setPostHogPersonMetadata, user, organizationLicense)
}

// Completes whatever `loadCookiePreference` deferred, once the real user is known.
//
// Visitors deferred above (`ferpaConsentDeferred`, a logged-in session pending its user record):
// run the full settle now — analytics/Rewardful/PostHog never ran at all for them yet.
//
// Visitors NOT deferred (anonymous at boot, incl. IP-matched — `hasUser` was false so
// `loadCookiePreference` already ran analytics/PostHog with `user: null`): only reconcile if
// this turns out to be a FERPA-covered org, revoking what was wrongly granted. Rewardful still
// needs firing either way, since `loadCookiePreference` never calls it while `user` is null.
export function* reconcileFerpaConsentOnUserLoad({ payload: user }) {
  if (ferpaConsentSettled) {
    return
  }

  const cookiePreference = yield select(({ ui }) => ui.cookiePreference)
  if (!cookiePreference || cookiePreference === 'unknown') {
    return
  }

  const effectivePreference = stripTargetingForFerpa(cookiePreference, user)

  if (ferpaConsentDeferred) {
    ferpaConsentSettled = true
    yield call(analytics.setup, effectivePreference)
    yield call(rewardful.initialize, effectivePreference)
    analytics.pageView()
    yield call(initializePostHog, effectivePreference)
    const organizationLicense = yield select(({ ui }) => ui.organizationLicense)
    yield call(setPostHogPersonMetadata, user, organizationLicense)
    return
  }

  if (user?.ferpaCovered) {
    ferpaConsentSettled = true
    yield call(analytics.setup, effectivePreference)
    yield call(initializePostHog, effectivePreference)
  }
  yield call(rewardful.initialize, effectivePreference)
}

const fetchChurnKey = function* () {
  const { data } = yield call(webApi.get, '/users/churnkey')
  yield put({
    type: constants.FETCH_CHURN_KEY_SUCCESS,
    payload: data
  })
}

const fetchFullTextLinkSaga = function* () {
  const batch = yield select(selectors.fullTextLinkQueue)
  if (!batch.length) {
    return
  }

  try {
    yield put({ type: constants.FULL_TEXT_QUEUE_CLEAR })
    const { data } = yield call(api.post, `${CONFIG.apiURL}/resolve-doi/batch`, batch)
    yield put({
      type: constants.FETCH_FULL_TEXT_LINK_SUCCESS,
      payload: data.items
    })
  } catch (e) {
    yield put({
      type: constants.FETCH_FULL_TEXT_LINK_FAILURE,
      error: e,
      payload: batch
    })
  }
}

const processAvailabilityQueue = function* () {
  const dois = yield select(selectors.agAvailabilityQueue)
  if (!dois.length) {
    return
  }

  try {
    yield put({ type: constants.AVAILABILITY_QUEUE_CLEAR })
    const { data } = yield call(api.post, `${CONFIG.apiURL}/article-galaxy/check-availability-batch`, { dois })
    yield put({
      type: constants.AVAILABILITY_QUEUE_SUCCESS,
      payload: data.items
    })
  } catch (error) {
    console.log(error)
  }
}

const updateAccessLogSaga = function* ({ payload: { doi, url } }) {
  try {
    yield call(api.get, `${CONFIG.apiURL}/resolve-doi/update-access-log?doi=${doi}&url=${url}`)
  } catch (e) {
    console.error(e)
  }
}

function* validateSession({ payload: { history, user } }) {
  try {
    yield call(webApi.get, '/users/me')
  } catch (e) {
    if (e.response?.status === 403 && e.response?.data === 'One session at a time.') {
      analytics.event({
        category: 'User',
        action: 'Autologged out',
        label: user?.email
      })
      clearAuthTokens()
      try {
        yield call(webApi.get, '/users/logout')
      } catch (e) {
        console.error('server logout failed, client session cleared anyway', e)
      }
      yield put({
        type: constants.LOGOUT_SUCCESS
      })
      history.push('/home')
      yield put(toggleModal('loggedOut', true))
    }
  }
}

export default function* rootSaga() {
  yield takeLatest(constants.TRIAL_ACTIVATED, errorWrap(trialActivated, constants.TRIAL_ACTIVATED_FAILURE))
  yield takeLatest(constants.GET_ENTITLEMENTS, errorWrap(getEntitlements, constants.GET_ENTITLEMENTS_FAILURE))
  yield takeLatest(constants.LOAD_USER, loadUser)
  yield takeLatest(constants.LOGOUT, errorWrap(logout, constants.LOGOUT_FAILURE))
  yield takeLatest(constants.SAVE_USER, errorWrap(saveUser, constants.SAVE_USER_FAILURE))
  yield takeLatest(constants.SAVE_REFERRALS, saveReferrals)
  yield takeLatest(constants.GET_NEW_TOKEN, getNewToken)
  yield takeLatest(constants.CHECK_PREMIUM_IP, errorWrap(checkOrganizationLicense, constants.CHECK_PREMIUM_IP_FAILURE))
  yield takeLatest(
    constants.DELETE_USER,
    errorWrap(deleteUser, constants.DELETE_USER_FAILURE, function* (e) {
      console.error('Unexpected error deleting user: ', e)
      errorToast({
        message:
          'Something went wrong deleted your account, try again or contact us at customersupport@researchsolutions.com',
        durationMs: 4000
      })
    })
  )
  yield takeLatest(constants.LOAD_ANON_TOKEN, loadAnonToken)
  yield takeLatest(
    constants.REENABLE_SUBSCRIPTION,
    errorWrap(reenableSubscription, constants.REENABLE_SUBSCRIPTION_ERROR)
  )
  yield takeLatest(constants.END_TRIAL_EARLY, errorWrap(endTrialEarly, constants.END_TRIAL_EARLY_ERROR))
  yield takeLatest(constants.FETCH_DISCOUNT, errorWrap(fetchDiscount, constants.FETCH_DISCOUNT_ERROR))
  yield takeLatest(constants.APPLY_DISCOUNT, errorWrap(applyDiscount, constants.APPLY_DISCOUNT_ERROR))
  yield takeLatest(
    constants.COMPLETE_ONBOARDING_STEP,
    errorWrap(completeOnboardingStep, constants.COMPLETE_ONBOARDING_FAILURE)
  )
  yield takeLatest(constants.FETCH_ONBOARDING, errorWrap(fetchOnboarding, constants.FETCH_ONBOARDING_FAILURE))

  yield takeLatest(constants.EXPORT_REFERENCES, errorWrap(exportReferences, constants.EXPORT_REFERENCES_FAILURE))

  yield takeLatest(constants.TOGGLE_MODAL, modalToggled)

  yield takeLatest(constants.LOAD_COOKIE_PREFERENCE, loadCookiePreference)
  yield takeLatest(constants.LOAD_USER_SUCCESS, reconcileFerpaConsentOnUserLoad)

  yield takeLatest(constants.FETCH_CHURN_KEY, errorWrap(fetchChurnKey, constants.FETCH_CHURN_KEY_FAILURE))

  yield debounce(
    500,
    constants.FETCH_AUTOCOMPLETES,
    errorWrap(fetchAutocompletes, constants.FETCH_AUTOCOMPLETES_FAILURE)
  )

  yield debounce(
    500,
    constants.FETCH_FULL_TEXT_LINK,
    errorWrap(fetchFullTextLinkSaga, constants.FETCH_FULL_TEXT_LINK_FAILURE)
  )

  yield debounce(500, constants.AVAILABILITY_QUEUE_APPEND, processAvailabilityQueue)

  yield takeLatest(constants.UPDATE_ACCESS_LOG, updateAccessLogSaga)

  yield throttle(60000, constants.VALIDATE_SESSION, validateSession)

  yield takeLatest(
    constants.GENERATE_BOOLEAN_SEARCH,
    errorWrap(generateBooleanSearch, constants.GENERATE_BOOLEAN_SEARCH_FAILURE)
  )

  yield takeLatest(
    constants.GENERATE_SUGGESTED_SEARCHES,
    errorWrap(generateSuggestedSearches, constants.GENERATE_SUGGESTED_SEARCHES_FAILURE)
  )
}
